import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { StylishNewCheckbox } from 'components/DesignSystems/New/StylishNewCheckbox';
import ManageTeamLocation from '../Notifications/ManageTeamLocation';
import { toast } from 'react-toastify';
import ReactPaginate from 'react-paginate';
import { AreaOfResponsibility } from '../../types/aor';
import { useTeamMemberFilters } from '../../hooks/useTeam';
import { Member, ICSTeam } from '../../types/teams';
import { getInitials } from '../../helpers/intials';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { AVAILABLE_ROLES } from 'components/IncidentConfig/AvailableRoles';

interface EditICSTeamDialogProps {
  roster: Member[];
  onClose: (team?: ICSTeam) => void;
  selectedICSTeam?: {
    members: string[];
    name: string;
    id: string;
    aor: AreaOfResponsibility | null;
  };
  icsTeams: ICSTeam[];
  show: boolean;
}

export default function EditICSTeamDialog({
  roster,
  onClose,
  selectedICSTeam: { members = [], name = '', id = '', aor = null } = {
    members: [],
    name: '',
    id: '',
    aor: null,
  },
  icsTeams,
  show,
}: EditICSTeamDialogProps) {
  const [selectedMembers, setSelectedMembers] = useState<Member[]>(
    roster.filter((member) => members.includes(member.user_guid))
  );
  const [selectedSeat, setSelectedSeat] = useState<{
    value: string;
    label: string;
  } | null>(name ? { value: name, label: name } : null);
  const [selectedAor, setSelectedAor] = useState<AreaOfResponsibility | null>(
    aor
  );
  const [searchTermInTeam, setSearchTermInTeam] = useState<string>('');
  const [searchTermAvailable, setSearchTermAvailable] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 6;
  const [currentPageInTeam, setCurrentPageInTeam] = useState<number>(1);

  const { membersInTeam, availableMembers } = useTeamMemberFilters({
    roster,
    selectedMembers,
    searchTermInTeam,
    searchTermAvailable,
  });

  const assignedMemberGuids = useMemo(() => {
    return icsTeams
      .filter((team) => team.id !== id)
      .flatMap((team) => team.members);
  }, [icsTeams, id]);
  const usedSeatNames = useMemo(() => {
    return icsTeams.filter((team) => team.id !== id).map((team) => team.name);
  }, [icsTeams, id]);

  const currentTeamMemberGuids = useMemo(() => members, [members]);

  const filteredAvailableMembers = useMemo(() => {
    return availableMembers.filter(
      (member) =>
        !assignedMemberGuids.includes(member.user_guid) ||
        currentTeamMemberGuids.includes(member.user_guid)
    );
  }, [availableMembers, assignedMemberGuids, currentTeamMemberGuids]);

  const seatOptions = useMemo(() => {
    return AVAILABLE_ROLES.filter((role) => !usedSeatNames.includes(role)).map(
      (role) => ({ value: role, label: role })
    );
  }, [usedSeatNames]);

  const totalPages = Math.ceil(filteredAvailableMembers.length / pageSize);
  const paginatedAvailableMembers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAvailableMembers.slice(start, start + pageSize);
  }, [filteredAvailableMembers, currentPage]);

  const totalPagesInTeam = Math.ceil(membersInTeam.length / pageSize);
  const paginatedMembersInTeam = useMemo(() => {
    const start = (currentPageInTeam - 1) * pageSize;
    return membersInTeam.slice(start, start + pageSize);
  }, [membersInTeam, currentPageInTeam]);

  useEffect(() => {
    setCurrentPageInTeam(1);
  }, [searchTermInTeam]);

  const handleMemberToggle = (member: Member): void => {
    const isSelected = selectedMembers.some(
      (m) => m.user_guid === member.user_guid
    );
    if (isSelected) {
      setSelectedMembers(
        selectedMembers.filter((m) => m.user_guid !== member.user_guid)
      );
    } else {
      setSelectedMembers([...selectedMembers, member]);
    }
  };

  const handleSelectAllAvailable = (): void => {
    const allSelected = availableMembers.every((member) =>
      selectedMembers.some((m) => m.user_guid === member.user_guid)
    );
    if (allSelected) {
      setSelectedMembers(
        selectedMembers.filter(
          (m) => !availableMembers.some((am) => am.user_guid === m.user_guid)
        )
      );
    } else {
      setSelectedMembers([
        ...selectedMembers,
        ...availableMembers.filter(
          (am) => !selectedMembers.some((m) => m.user_guid === am.user_guid)
        ),
      ]);
    }
  };

  const onSaveTeamClick = (): void => {
    if (!selectedSeat) {
      toast.error('Incident Seat is required');
      return;
    }
    if (selectedMembers.length === 0) {
      toast.error('At least one member is required');
      return;
    }
    const newTeam: ICSTeam = {
      id,
      name: selectedSeat.value,
      members: selectedMembers.map((member) => member.user_guid),
      nonMembers: [],
      aor: selectedAor,
    };
    const listWithoutCurrentEle = icsTeams.filter(
      (ele) => ele.id !== newTeam.id
    );
    const currentTeam = listWithoutCurrentEle.findIndex(
      (ele) => ele.name === newTeam.name
    );
    if (currentTeam !== -1) {
      const errorMessage = `Duplicate ICS Team Seat.\n\nFailed Update ICS Team`;
      console.warn(errorMessage);
      toast.error(errorMessage);
      return;
    } else {
      onClose(newTeam);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => onClose()}
      dialogClassName="modal-90w"
      aria-labelledby="edit-ics-team-modal-title"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title id="edit-ics-team-modal-title">Edit ICS Team</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="edit-ics-team-form">
          <div className="team-info-section">
            <div className="form-group">
              <label className="form-label">Incident Seat</label>
              <StylishNewSelect
                options={seatOptions}
                value={selectedSeat}
                onChange={setSelectedSeat}
                placeholder="Select Incident Seat"
                isClearable={true}
                isSearchable={true}
                isMulti={false}
                isDisabled={false}
              />
            </div>

            <div className="form-group">
              <ManageTeamLocation
                selectedAor={selectedAor}
                onChangeAor={setSelectedAor}
              />
            </div>
          </div>

          <div className="member-selection-section">
            <div className="members-in-team-section">
              <label className="form-label">Members in Team</label>
              <StylishNewSearchBar
                value={searchTermInTeam}
                onChangeSearchTerm={setSearchTermInTeam}
                placeholder="Search Members"
                className="member-search"
              />
              <div className="members-list">
                {paginatedMembersInTeam.length === 0 && (
                  <div className="empty-list">No members in team.</div>
                )}
                {paginatedMembersInTeam.map((member) => (
                  <div key={member.user_guid} className="member-item">
                    <StylishNewCheckbox
                      checked={true}
                      onClick={() => handleMemberToggle(member)}
                    />
                    <div className="member-avatar">
                      <div className="rounded-circle avatar-initials sml">
                        {getInitials(
                          member.user_name || member.email_address || ''
                        )}
                      </div>
                    </div>
                    <div className="member-info">
                      <div className="member-main-row">
                        <span className="member-name">
                          {member.user_name || '-'}
                        </span>
                        <span className="member-dot-separator">&bull;</span>
                        <span className="member-email">
                          {member.email_address || '-'}
                        </span>
                      </div>
                      <div className="member-role">
                        {member.role_string || '-'}
                      </div>
                    </div>
                  </div>
                ))}
                {totalPagesInTeam > 1 && (
                  <div className="d-flex justify-content-center justify-content-md-end mt-4">
                    <ReactPaginate
                      className="pagination"
                      breakLabel="..."
                      nextLabel=">"
                      previousLabel="<"
                      onPageChange={({ selected }) =>
                        setCurrentPageInTeam(selected + 1)
                      }
                      pageRangeDisplayed={3}
                      pageCount={totalPagesInTeam}
                      forcePage={currentPageInTeam - 1}
                      renderOnZeroPageCount={null}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="available-members-section">
              <label className="form-label">Available Members</label>
              <StylishNewSearchBar
                value={searchTermAvailable}
                onChangeSearchTerm={setSearchTermAvailable}
                placeholder="Search Members"
                className="member-search"
              />
              <div className="select-all-container">
                <button
                  type="button"
                  className="select-all-link"
                  onClick={handleSelectAllAvailable}
                >
                  Select All
                </button>
              </div>
              <div className="members-list">
                {paginatedAvailableMembers.length === 0 && (
                  <div className="empty-list">No available members.</div>
                )}
                {paginatedAvailableMembers.map((member) => {
                  const isSelected = selectedMembers.some(
                    (m) => m.user_guid === member.user_guid
                  );
                  return (
                    <div key={member.user_guid} className="member-item">
                      <StylishNewCheckbox
                        checked={isSelected}
                        onClick={() => handleMemberToggle(member)}
                      />
                      <div className="member-avatar">
                        <div className="rounded-circle avatar-initials sml">
                          {getInitials(
                            member.user_name || member.email_address || ''
                          )}
                        </div>
                      </div>
                      <div className="member-info">
                        <div className="member-main-row">
                          <span className="member-name">
                            {member.user_name || '-'}
                          </span>
                          <span className="member-dot-separator">&bull;</span>
                          <span className="member-email">
                            {member.email_address || '-'}
                          </span>
                        </div>
                        <div className="member-role">
                          {member.role_string || '-'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center justify-content-md-end mt-4">
                  <ReactPaginate
                    className="pagination"
                    breakLabel="..."
                    nextLabel=">"
                    previousLabel="<"
                    onPageChange={({ selected }) =>
                      setCurrentPage(selected + 1)
                    }
                    pageRangeDisplayed={3}
                    pageCount={totalPages}
                    forcePage={currentPage - 1}
                    renderOnZeroPageCount={null}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <StylishNewButton
          className="button--secondary"
          onClick={() => onClose()}
        >
          Cancel
        </StylishNewButton>
        <StylishNewButton className="button--primary" onClick={onSaveTeamClick}>
          Save
        </StylishNewButton>
      </Modal.Footer>
    </Modal>
  );
}
